import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { ChargePointLog } from '../../../../../../../app/ApiGen';
import { ColumnFiltersToggle, RefreshButton } from '../../../../../../../components/DataTable/Toolbar';
import { DateRangePicker } from '../../../../../../../components/DateRange';
import { ExportCsv, type ExportProps } from '../../../../../../../components/ExportCsv';
import {
  Card,
  ColumnChanger,
  ColumnChangerProps,
  DataTable,
  SearchField,
  SearchFieldProps,
  Section,
} from '../../../../../../../design-system';
import { ColumnDefinitions } from '../../../../../../../utils/dataTable/columns';
import { DateFormat } from '../../../../../../../utils/dateFormat';
import type { DateRangeProps } from '../../../../../../../utils/dateRange';
import { Normalised } from '../../../../../../../utils/request';
import { includesSearch } from '../../../../../../../utils/search';
import { sortByDate } from '../../../../../../../utils/sortBy';
import { csvDateFormat } from '../../../../Reports/utils';
import { renderLogData, TableChargePointLog } from '../util';
import { renderDate, renderMessageId } from './tableUtils';

interface ChargePointLogsTableProps<Data> {
  loading: boolean;
  refetch: () => Promise<Data | null>;
  chargePointLogs: Normalised<ChargePointLog>[];
  title?: string;
  description?: string;
  exportFileName?: string;
}

const getCsvFieldValue =
  (
    getFieldValue: ExportProps<TableChargePointLog>['getFieldValue'],
  ): ExportProps<TableChargePointLog>['getFieldValue'] =>
  (row, column) => {
    const value = getFieldValue(row, column);
    return typeof value === 'object' && value !== null ? JSON.stringify(value) : value;
  };

const columns: ColumnDefinitions<TableChargePointLog, keyof TableChargePointLog> = {
  serverEventDate: {
    title: 'Server timestamp',
    field: 'serverEventDate',
    type: 'date',
    render: ({ serverEventDate }) => renderDate(serverEventDate),
    customSort: sortByDate('serverEventDate'),
    defaultSort: 'desc',
    filtering: false,
  },
  chargePointEventDate: {
    title: 'Charge point timestamp',
    field: 'chargePointEventDate',
    type: 'date',
    render: ({ chargePointEventDate }) => renderDate(chargePointEventDate),
    customSort: sortByDate('chargePointEventDate'),
    filtering: false,
  },
  messageId: {
    title: 'Message ID',
    field: 'messageId',
    render: ({ messageId }) => renderMessageId(messageId),
    filtering: false,
  },
  eventType: { title: 'Event', field: 'eventType' },
  eventPayload: {
    title: 'Data',
    field: 'eventPayload',
    hidden: true,
    sorting: false,
    filtering: true,
    searchable: true,
    render: renderLogData,
    customFilterAndSearch: (filter: unknown, { eventPayload }) => includesSearch(JSON.stringify(eventPayload), filter),
  },
};

export function ChargePointLogsTable<Data>({
  dateProps,
  tableProps,
}: {
  tableProps: ChargePointLogsTableProps<Data>;
  dateProps: DateRangeProps;
}) {
  const [filtering, setFiltering] = React.useState(false);

  const { chargePointLogs, description, exportFileName, loading, refetch, title } = tableProps;

  const {
    dateRange: { fromDate, toDate },
    setDateRange,
  } = dateProps;

  const fromFormatted = moment(fromDate).format(DateFormat.Filename);
  const toFormatted = moment(toDate).format(DateFormat.Filename);
  const exportFileNameWithRange = `${exportFileName ?? 'charge-point-logs'}-from-${fromFormatted}-to-${toFormatted}`;

  return (
    <Section
      title={title ?? 'Charge point logs'}
      description={
        description ??
        'View messages sent and received by the system from the charge point. ' +
          'Note that if you select a very wide range, the request is more likely to time out.'
      }
    >
      <Card>
        <DataTable<TableChargePointLog>
          isLoading={loading}
          options={{ columnsButton: true, filtering }}
          toolbarProps={{
            search: (props: SearchFieldProps) => (
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ paddingBottom: 8 }}>
                  <DateRangePicker fromDate={fromDate} toDate={toDate} onApply={setDateRange} />
                </span>
                <SearchField
                  dataManager={props.dataManager}
                  item="logs"
                  onSearchChanged={props.onSearchChanged}
                  search={props.search}
                />
              </span>
            ),
            actions: (props: PropsWithChildren<ColumnChangerProps> & ExportProps<TableChargePointLog>) => (
              <>
                <ColumnChanger
                  columnsButton={props.columnsButton}
                  columns={props.columns}
                  icons={props.icons}
                  onColumnsChanged={props.onColumnsChanged}
                />
                <ColumnFiltersToggle filtering={filtering} setFiltering={setFiltering} />
                <RefreshButton refetch={refetch} />
                <ExportCsv<TableChargePointLog>
                  columns={props.columns}
                  data={props.data}
                  dateFormat={csvDateFormat}
                  exportFileName={exportFileNameWithRange}
                  getFieldValue={getCsvFieldValue(props.getFieldValue)}
                  icons={props.icons}
                />
              </>
            ),
          }}
          columns={Object.values(columns)}
          data={cloneDeep(chargePointLogs)}
          detailPanel={renderLogData}
        />
      </Card>
    </Section>
  );
}
